import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { StatusBadge } from '@components/common';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

const BorrowingValidation = ({ borrowing, permissions, onValidationComplete }) => {
    const { t, currentLanguage } = useLanguage();
    const notify = useNotification();
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationNotes, setValidationNotes] = useState('');

    const handleValidation = async (validationStatus) => {
        if (!borrowing || validationLoading) return;

        try {
            setValidationLoading(true);
            await api.borrowings.validate(borrowing.id, {
                status: validationStatus,
                validation_notes: validationNotes || null
            });

            setValidationNotes('');

            const msgKey = validationStatus === 'validated'
                ? 'borrowing.validation.validated'
                : 'borrowing.validation.rejected';
            notify.success(t(msgKey));

            // Notify parent to reload
            if (onValidationComplete) {
                onValidationComplete();
            }
        } catch (err) {
            console.error('Failed to validate reservation:', err);
            notify.error(t('borrowing.validation.error'));
        } finally {
            setValidationLoading(false);
        }
    };

    if (!borrowing.requires_validation || !borrowing.validation_status) {
        return null;
    }

    return (
        <div className="validation-section">
            <h3>{t('borrowing.validation.title')}</h3>

            <div className="validation-status-info">
                <span className="validation-label">{t('borrowing.validation.currentStatus')}</span>
                <StatusBadge model={borrowing} type="borrowing" className="size-small" />
            </div>

            {borrowing.validator_info && (
                <div className="validation-info">
                    <span className="validation-label">{t('borrowing.validation.validatedBy')}</span>
                    <span className="validation-value">{borrowing.validator_info.name || borrowing.validator_info.email}</span>
                </div>
            )}

            {borrowing.validated_at && (
                <div className="validation-info">
                    <span className="validation-label">{t('borrowing.validation.validatedAt')}</span>
                    <span className="validation-value">{new Date(borrowing.validated_at).toLocaleString(currentLanguage)}</span>
                </div>
            )}

            {borrowing.validation_notes && (
                <div className="validation-info">
                    <span className="validation-label">{t('borrowing.validation.notes')}</span>
                    <span className="validation-value">{borrowing.validation_notes}</span>
                </div>
            )}

            {/* Validation actions - only if user can validate and status is pending/rejected */}
            {permissions.canValidate && (borrowing.validation_status === 'pending' || borrowing.validation_status === 'rejected') && (
                <div className="validation-actions">
                    <div className="validation-notes-input">
                        <label>{t('borrowing.validation.notesLabel')}</label>
                        <textarea
                            value={validationNotes}
                            onChange={(e) => setValidationNotes(e.target.value)}
                            placeholder={t('borrowing.validation.notesPlaceholder')}
                            rows={2}
                            disabled={validationLoading}
                        />
                    </div>
                    <div className="validation-buttons">
                        <button
                            className="btn btn-success"
                            onClick={() => handleValidation('validated')}
                            disabled={validationLoading}
                        >
                            {t('borrowing.validation.approve')}
                        </button>
                        <button
                            className="btn btn-danger"
                            onClick={() => handleValidation('rejected')}
                            disabled={validationLoading}
                        >
                            {t('borrowing.validation.reject')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BorrowingValidation;
